/**
 * Composer length guard (#452), learned from the server.
 *
 * `POST /api/chat` rejects a message longer than `CHAT_MESSAGE_MAX_LENGTH` with
 * a 400 that the transcript renders after the user has already typed and sent.
 * The composer guards against that up front — but it must not keep its own copy
 * of the number, because a deployment that LOWERS the server limit would leave
 * the client happily accepting messages the server refuses, which is the exact
 * failure #384/#470 hit with the search minimum.
 *
 * So this mirrors that pattern: a fallback used only until `GET
 * /api/auth/status` has been read, then the server's advertised
 * `chatMessageMaxLength` takes over. The server remains the boundary; this is
 * only an affordance.
 */
export const CHAT_MESSAGE_MAX_LENGTH_FALLBACK = 10000;

let effectiveChatMessageMaxLength = CHAT_MESSAGE_MAX_LENGTH_FALLBACK;

/**
 * Adopt the server's configured maximum. Called by `authService` whenever
 * `/api/auth/status` is read. Anything that is not a whole number >= 1 is
 * ignored, so a malformed or absent field leaves the fallback in place.
 */
export function setEffectiveChatMessageMaxLength(value: unknown): void {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 1) {
    effectiveChatMessageMaxLength = value;
  }
}

/** The maximum currently in force: the server's if known, else the fallback. */
export function getEffectiveChatMessageMaxLength(): number {
  return effectiveChatMessageMaxLength;
}

/** Test-only: forget anything learned from the server. */
export function resetEffectiveChatMessageMaxLength(): void {
  effectiveChatMessageMaxLength = CHAT_MESSAGE_MAX_LENGTH_FALLBACK;
}

/**
 * Length exactly as the SERVER counts it.
 *
 * The server is the authority: `chatValidators` runs express-validator's
 * `isLength`, which does NOT count code points. It takes
 * `String.prototype.length` (UTF-16 code units) and subtracts two things:
 *
 *   1. surrogate pairs        — `/[\uD800-\uDBFF][\uDC00-\uDFFF]/g`
 *   2. presentation sequences — `/[^\uFE0F\uFE0E][\uFE0F\uFE0E]/g`, i.e. a
 *      character followed by variation selector 16/15
 *
 * Counting code points (`[...text].length`) agrees with that for a bare
 * surrogate pair such as '👍', but NOT for a VS16 emoji such as '❤️' (U+2764
 * U+FE0F): the server counts it as 1, code points count it as 2. That made the
 * client STRICTER than the server — 10 000 '❤️' are accepted by the server at
 * the default 10 000 limit while the composer reported them as 20 000, showed
 * "10,000 characters over the 10,000 limit" and disabled send on input the
 * server would have taken.
 *
 * A client stricter than the server silently blocks legitimate messages; a
 * client more permissive reinstates the post-send 400 this guard exists to
 * avoid. They must agree, so this mirrors `isLength` verbatim.
 *
 * Counts the string exactly as given; callers comparing against the limit must
 * hand it what the server will see, i.e. the trimmed text — see
 * `serverMeasuredLength` below.
 *
 * @see node_modules/validator/lib/isLength.js
 */
export function chatMessageLength(text: string): number {
  const presentationSequences = text.match(/[^\uFE0F\uFE0E][\uFE0F\uFE0E]/g) ?? [];
  const surrogatePairs = text.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) ?? [];
  return text.length - presentationSequences.length - surrogatePairs.length;
}

/**
 * What the server will actually measure: the chain is
 * `notEmpty().trim().isLength(...)`, so `isLength` sees the TRIMMED string, and
 * `useChatStream` sends `value.trim()` too. Measuring the raw text would make
 * the client stricter than the server again — `'a'.repeat(10000) + '   '`
 * counts 10 003 raw but 10 000 once trimmed, so the composer would disable send
 * on a message the server accepts. Trailing spaces are ordinary (a pause
 * mid-thought, a paste artefact), so this must not be a guess.
 */
function serverMeasuredLength(text: string): number {
  return chatMessageLength(text.trim());
}

/** True when `text` is longer than the server will accept. */
export function isChatMessageTooLong(text: string): boolean {
  return serverMeasuredLength(text) > effectiveChatMessageMaxLength;
}

/**
 * Copy for an over-length composer, or null when the message fits. Names the
 * overshoot so trimming is actionable rather than guesswork.
 */
export function describeChatMessageTooLong(text: string): string | null {
  const length = serverMeasuredLength(text);
  const max = effectiveChatMessageMaxLength;
  if (length <= max) {
    return null;
  }
  const over = length - max;
  return `Message is ${over.toLocaleString()} character${over === 1 ? '' : 's'} over the ${max.toLocaleString()} limit. Shorten it or split it into two messages.`;
}
